/**
 * Single source of truth for the reporting-v2 dashboard list.
 *
 * Six components used to call `listDashboards` independently and keep their
 * own copy in state: Navbar, ReportingV2Home, DashboardView, WidgetBuilderV2,
 * WidgetLibrary and WidgetDataModal. That meant a request per component per
 * org switch, and — worse — a dashboard created in one of them was invisible
 * to the others until a custom `dashboardsV2Changed` window event was
 * dispatched and separately listened for.
 *
 * `listDashboards` coalesces *in-flight* calls, which helps when components
 * mount simultaneously, but the entry is dropped once the promise settles, so
 * staggered mounts still each fetch and none of them share state.
 *
 * The provider fetches once per organisation and exposes `refresh()`, which
 * mutations call instead of patching their own copy and broadcasting. The
 * window-event listener is kept only as a safety net for callers outside
 * this module; nothing in frontend/src dispatches it any more.
 *
 * The provider wraps the whole authenticated tree, but the endpoint behind
 * it is gated by `@require_feature("reporting")`, so the fetch is gated on
 * the same feature. Without that, every user whose plan or role excludes
 * reporting would fire a guaranteed 403 on each login and org switch.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth } from './AuthContext';
import { usePermissions } from './PermissionContext';
import { listDashboards } from '../services/api/reporting';
import type { SavedDashboardV2 } from '../services/api/reporting';

/** Feature key gating /api/reporting/dashboards/ on the backend. */
const REPORTING_FEATURE = 'reporting';

export interface DashboardsContextValue {
  dashboards: SavedDashboardV2[];
  loading: boolean;
  error: string | null;
  /** Refetch for the current organisation and update every consumer. */
  refresh: () => Promise<void>;
}

const EMPTY: SavedDashboardV2[] = [];

// No default value: useDashboards throws when the provider is absent rather
// than quietly reporting "no dashboards", which would turn a mis-mounted
// component into an empty screen instead of a visible failure.
const DashboardsContext = createContext<DashboardsContextValue | null>(null);

export const DashboardsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Keyed on the selected organisation alone: an organisation can only be
  // selected while authenticated, and clearing auth clears the selection.
  const { selectedOrganisationId } = useAuth();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const orgId = selectedOrganisationId ? String(selectedOrganisationId) : '';
  const canUseReporting = hasFeature(REPORTING_FEATURE);

  const [dashboards, setDashboards] = useState<SavedDashboardV2[]>(EMPTY);
  // Starts true so consumers that branch on the list (ReportingV2Home picks
  // between redirecting to the first dashboard and showing the builder)
  // render their loading state on the first paint, before the effect that
  // starts the fetch has run.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Guards against a slow response for a previous org overwriting a newer
  // one after an org switch.
  const requestOrgRef = useRef('');

  const load = useCallback(async () => {
    // Permissions not resolved yet — stay loading rather than concluding
    // "no reporting access" and skipping the fetch for good.
    if (permissionsLoading) {
      setLoading(Boolean(orgId));
      return;
    }

    if (!orgId || !canUseReporting) {
      requestOrgRef.current = '';
      setDashboards(EMPTY);
      setError(null);
      setLoading(false);
      return;
    }

    requestOrgRef.current = orgId;
    setLoading(true);
    setError(null);

    try {
      const list = await listDashboards(orgId);
      if (requestOrgRef.current !== orgId) return;
      setDashboards(list || EMPTY);
    } catch (err: any) {
      if (requestOrgRef.current !== orgId) return;
      setDashboards(EMPTY);
      setError(err?.message || 'Failed to load dashboards');
    } finally {
      if (requestOrgRef.current === orgId) setLoading(false);
    }
  }, [orgId, canUseReporting, permissionsLoading]);

  useEffect(() => {
    load();
  }, [load]);

  // Safety net for any code outside this module that still signals a change
  // by dispatching the legacy window event. Everything in frontend/src now
  // calls refresh() directly instead.
  useEffect(() => {
    const handler = () => {
      load();
    };
    window.addEventListener('dashboardsV2Changed', handler);
    return () => window.removeEventListener('dashboardsV2Changed', handler);
  }, [load]);

  const value = useMemo<DashboardsContextValue>(
    () => ({ dashboards, loading, error, refresh: load }),
    [dashboards, loading, error, load],
  );

  return (
    <DashboardsContext.Provider value={value}>
      {children}
    </DashboardsContext.Provider>
  );
};

/**
 * Read the shared dashboard list.
 *
 * Throws when no provider is mounted. A component rendered outside the
 * provider would otherwise report "no dashboards" and render an empty
 * screen, which looks like ordinary empty state rather than the wiring
 * mistake it is. Tests should use `renderWithProviders` from
 * `src/test-utils/renderWithProviders` rather than a bare `render`.
 */
export function useDashboards(): DashboardsContextValue {
  const value = useContext(DashboardsContext);

  if (value === null) {
    throw new Error(
      'useDashboards must be used within a <DashboardsProvider>. In tests, ' +
        'render via renderWithProviders from src/test-utils/renderWithProviders.',
    );
  }

  return value;
}

export default DashboardsContext;
